import React from 'react';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Stack,
  Box,
  Alert,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  AccessTime,
  Gavel,
  Warning,
} from '@mui/icons-material';
import { formatDistanceToNow, format, isPast } from 'date-fns';
import { OfferRoundWithRelations } from '../../api/services/offerRounds';
import { RoundStatus, InitiatorType } from '@prisma/client';
import { formatCurrency } from '../../utils/formatCurrency';
import useIsMobile from '../../hooks/useIsMobile';

interface NegotiationTimelineProps {
  rounds: OfferRoundWithRelations[];
  currentUserId: string;
  listingOwnerId: string;
  isPublicView?: boolean;
}

export const NegotiationTimeline: React.FC<NegotiationTimelineProps> = ({
  rounds,
  currentUserId,
  listingOwnerId: _listingOwnerId,
  isPublicView = false,
}) => {
  const isMobile = useIsMobile();
  const getStatusIcon = (status: RoundStatus) => {
    const iconStyle = { fontSize: 20 };
    switch (status) {
      case RoundStatus.ACCEPTED:
        return <CheckCircle sx={{ color: '#4caf50', ...iconStyle }} />;
      case RoundStatus.REJECTED:
        return <Cancel sx={{ color: '#f44336', ...iconStyle }} />;
      case RoundStatus.EXPIRED:
        return <Warning sx={{ color: '#ff9800', ...iconStyle }} />;
      case RoundStatus.COUNTERED:
        return <Gavel sx={{ color: '#2196f3', ...iconStyle }} />;
      default:
        return <AccessTime sx={{ color: '#757575', ...iconStyle }} />;
    }
  };

  // Removed unused function _getStatusColor

  const getInitiatorInfo = (round: OfferRoundWithRelations) => {
    const isBuyer = round.initiatorType === InitiatorType.BUYER;

    // For public view (listing agent via token), their rounds have initiatedBy: null
    const isCurrentUser = isPublicView
      ? round.initiatedBy === null && !isBuyer // Public seller rounds
      : round.initiatedBy === currentUserId; // Authenticated user rounds

    return {
      label: isBuyer ? 'Buyer' : 'Seller',
      isYou: isCurrentUser,
      name: round.initiator
        ? `${round.initiator.firstName} ${round.initiator.lastName}`
        : isBuyer
        ? 'Buyer'
        : 'Listing Agent', // Show appropriate name for public rounds
    };
  };

  const formatFieldValue = (value: any, field: string): string => {
    switch (field) {
      case 'price':
      case 'earnestMoney':
      case 'concessions':
      case 'loan':
        return formatCurrency(value || 0);
      case 'closingDate':
        return format(new Date(value), 'MMM d, yyyy');
      case 'appliances':
      case 'contingent':
      case 'termiteInspection':
      case 'termiteBond':
      case 'sewerInspection':
      case 'newSurvey':
        return value ? 'Yes' : 'No';
      case 'bagentComm':
        return `${value}%`;
      case 'inspectionPeriod':
        return `${value} days`;
      case 'paymentMethod':
        return value;
      case 'homeWarrantyType':
        return value === 'NONE'
          ? 'None'
          : value === 'SELLER'
          ? 'Seller Provided'
          : 'Buyer Provided';
      default:
        return String(value || 'N/A');
    }
  };

  const formatChangedFields = (
    round: OfferRoundWithRelations,
    isMobile: boolean
  ): React.ReactNode => {
    const changedData = round.changedFields as Record<string, any> | null;
    if (!changedData || typeof changedData !== 'object') return 'No changes';

    const fields = Object.keys(changedData);
    if (fields.length === 0) return 'No changes';

    const fieldLabels: Record<string, string> = {
      price: 'Price',
      closingDate: 'Closing Date',
      earnestMoney: 'Earnest Money',
      concessions: 'Concessions',
      appliances: 'Appliances',
      inspectionPeriod: 'Inspection Period',
      contingent: 'Contingent Sale',
      bagentComm: 'Buyer Agent Commission',
      loan: 'Loan Amount',
      homeWarrantyType: 'Home Warranty',
      termiteInspection: 'Termite Inspection',
      termiteBond: 'Termite Bond',
      sewerInspection: 'Sewer Inspection',
      newSurvey: 'New Survey',
      additionalTerms: 'Additional Terms',
      paymentMethod: 'Payment Method',
    };

    // Separate important fields from others
    const importantFields = ['price', 'closingDate', 'paymentMethod', 'loan'];
    const primaryChanges = fields.filter((f) => importantFields.includes(f));
    const secondaryChanges = fields.filter((f) => !importantFields.includes(f));

    return (
      <Stack spacing={1}>
        {primaryChanges.length > 0 && (
          <Stack spacing={0.75}>
            {primaryChanges.map((field) => (
              <Box key={field} display="flex" alignItems="center" gap={1}>
                <Typography
                  variant="body2"
                  component="span"
                  sx={{
                    color: 'text.secondary',
                    minWidth: isMobile ? '80px' : '120px',
                    fontSize: isMobile ? '0.8125rem' : '0.875rem',
                    textAlign: 'left',
                  }}
                >
                  {fieldLabels[field] || field}
                </Typography>
                <Typography
                  variant="body2"
                  component="span"
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    fontSize: isMobile ? '0.8125rem' : '0.875rem',
                  }}
                >
                  {formatFieldValue((round as any)[field], field)}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
        {secondaryChanges.length > 0 && (
          <Stack spacing={0.5}>
            {secondaryChanges.map((field) => (
              <Box key={field} display="flex" alignItems="center" gap={1}>
                <Typography
                  variant="body2"
                  component="span"
                  sx={{
                    color: 'text.secondary',
                    minWidth: isMobile ? '80px' : '120px',
                    fontSize: isMobile ? '0.75rem' : '0.8125rem',
                    textAlign: 'left',
                  }}
                >
                  {fieldLabels[field] || field}
                </Typography>
                <Typography
                  variant="body2"
                  component="span"
                  sx={{
                    fontWeight: 500,
                    color: 'text.primary',
                    fontSize: isMobile ? '0.75rem' : '0.8125rem',
                  }}
                >
                  {formatFieldValue((round as any)[field], field)}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
      </Stack>
    );
  };

  const sortedRounds = [...rounds].sort(
    (a, b) => b.roundNumber - a.roundNumber
  );

  return (
    <Timeline
      position={isMobile ? 'right' : 'alternate'}
      sx={{
        '& .MuiTimelineContent-root': { textAlign: 'left' },
        ...(isMobile && {
          padding: 0,
          '& .MuiTimelineOppositeContent-root': {
            display: 'none',
          },
          '& .MuiTimelineItem-root:before': {
            flex: 0,
            padding: 0,
          },
        }),
      }}
    >
      {sortedRounds.map((round, index) => {
        const initiatorInfo = getInitiatorInfo(round);
        const isExpired =
          round.status === RoundStatus.PENDING &&
          isPast(new Date(round.expiresAt));

        return (
          <TimelineItem key={round.id}>
            {!isMobile && (
              <TimelineOppositeContent sx={{ m: 'auto 0' }}>
                <Typography variant="body2" color="text.secondary">
                  {format(new Date(round.createdAt), 'MMM d, h:mm a')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatDistanceToNow(new Date(round.createdAt), {
                    addSuffix: true,
                  })}
                </Typography>
              </TimelineOppositeContent>
            )}

            <TimelineSeparator>
              <TimelineConnector sx={{ bgcolor: 'grey.300' }} />
              <TimelineDot sx={{ bgcolor: 'transparent', boxShadow: 'none' }}>
                {getStatusIcon(isExpired ? RoundStatus.EXPIRED : round.status)}
              </TimelineDot>
              {index < sortedRounds.length - 1 && (
                <TimelineConnector sx={{ bgcolor: 'grey.300' }} />
              )}
            </TimelineSeparator>

            <TimelineContent sx={{ py: '12px', px: isMobile ? 1 : 2 }}>
              <Card elevation={isMobile ? 1 : 2} sx={{ width: '100%' }}>
                <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
                  <Stack spacing={isMobile ? 1.5 : 2}>
                    {/* Header */}
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems={isMobile ? 'flex-start' : 'center'}
                      flexDirection={isMobile ? 'column' : 'row'}
                      gap={isMobile ? 1 : 0}
                    >
                      <Box>
                        <Typography variant="subtitle2">
                          Counter {round.roundNumber}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {initiatorInfo.label} {initiatorInfo.isYou && '(You)'}
                        </Typography>
                        {isMobile && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            {format(new Date(round.createdAt), 'MMM d, h:mm a')}
                          </Typography>
                        )}
                      </Box>
                      <Chip
                        label={
                          isExpired
                            ? 'Expired'
                            : round.status === RoundStatus.PENDING
                            ? 'Awaiting response'
                            : round.status === RoundStatus.ACCEPTED
                            ? 'Accepted'
                            : round.status === RoundStatus.REJECTED
                            ? 'Rejected'
                            : round.status === RoundStatus.COUNTERED
                            ? 'Countered'
                            : round.status
                        }
                        size="small"
                        sx={{
                          fontWeight: 500,
                          ...(isExpired && {
                            backgroundColor: '#fff3e0',
                            color: '#e65100',
                            border: '1px solid #ffcc80',
                          }),
                          ...(round.status === RoundStatus.PENDING && {
                            backgroundColor: '#e3f2fd',
                            color: '#1565c0',
                            border: '1px solid #90caf9',
                          }),
                          ...(round.status === RoundStatus.ACCEPTED && {
                            backgroundColor: '#e8f5e9',
                            color: '#2e7d32',
                            border: '1px solid #81c784',
                          }),
                          ...(round.status === RoundStatus.REJECTED && {
                            backgroundColor: '#ffebee',
                            color: '#c62828',
                            border: '1px solid #ef9a9a',
                          }),
                          ...(round.status === RoundStatus.COUNTERED && {
                            backgroundColor: '#f3e5f5',
                            color: '#6a1b9a',
                            border: '1px solid #ba68c8',
                          }),
                        }}
                      />
                    </Box>

                    {/* Changed Fields as main content */}
                    {(() => {
                      const changedData = round.changedFields as Record<
                        string,
                        any
                      > | null;
                      const hasChangedFields =
                        changedData && Object.keys(changedData).length > 0;

                      if (hasChangedFields) {
                        return (
                          <Box
                            sx={{
                              bgcolor: 'grey.50',
                              p: isMobile ? 1 : 1.5,
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'grey.200',
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                mb: 0.5,
                                fontSize: '0.875rem',
                                textAlign: 'left',
                              }}
                            >
                              <strong>Changed terms:</strong>
                            </Typography>
                            {formatChangedFields(round, isMobile)}
                          </Box>
                        );
                      } else if (round.roundNumber === 1) {
                        // First counter
                        return (
                          <Box
                            sx={{
                              bgcolor: 'grey.50',
                              p: isMobile ? 1 : 1.5,
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'grey.200',
                            }}
                          >
                            <Typography variant="body2">
                              <strong>Counter 1</strong>
                            </Typography>
                          </Box>
                        );
                      } else {
                        // Subsequent counters without changed fields
                        return (
                          <Box
                            sx={{
                              bgcolor: 'grey.50',
                              p: isMobile ? 1 : 1.5,
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'grey.200',
                            }}
                          >
                            <Typography variant="body2">
                              <strong>Counter {round.roundNumber}</strong>
                            </Typography>
                          </Box>
                        );
                      }
                    })()}

                    {/* Response Notes */}
                    {round.responseNotes && (
                      <Box
                        sx={{
                          bgcolor: 'grey.50',
                          p: isMobile ? 0.75 : 1,
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          &quot;{round.responseNotes}&quot;
                        </Typography>
                      </Box>
                    )}

                    {/* Expiration Warning */}
                    {round.status === RoundStatus.PENDING && !isExpired && (
                      <Alert
                        severity="warning"
                        sx={{ py: isMobile ? 0.25 : 0.5 }}
                      >
                        <Typography variant="caption">
                          Expires{' '}
                          {new Date(round.expiresAt).toLocaleString(undefined, {
                            dateStyle: isMobile ? 'short' : 'medium',
                            timeStyle: 'short',
                          })}
                        </Typography>
                      </Alert>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </TimelineContent>
          </TimelineItem>
        );
      })}
    </Timeline>
  );
};
